/**
 * Página de Relatório de OS por Área
 * 
 * Exibe relatório de Ordens de Serviço separadas por área da empresa
 * com informações de comentários, horas trabalhadas, demandas e projetos
 * 
 * @author Equipe GestaoeCRM
 * @date 02/12/2025
 */

import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { FileText, Download, RefreshCw, Filter, ChevronDown, ChevronUp, Tag } from 'lucide-react';
import { osReportsApi, OSByAreaReport } from '../services/osReportsApi';
import companyAreaService from '../services/companyAreaService';
import { usersApi } from '../services/api';
import { updateOS } from '../services/osApi';
import LoadingSpinner from '../components/LoadingSpinner';
import toast from 'react-hot-toast';
import jsPDF from 'jspdf';

export default function OSByAreaReportPage() {
  const [selectedAreaId, setSelectedAreaId] = useState<string>('all');
  const [selectedUserId, setSelectedUserId] = useState<string>('all');
  const [expandedAreas, setExpandedAreas] = useState<Set<string>>(new Set());
  const [classifyingOS, setClassifyingOS] = useState<string | null>(null);
  const [selectedAreaForOS, setSelectedAreaForOS] = useState<Record<string, string>>({});
  const queryClient = useQueryClient();

  // Buscar áreas disponíveis
  const { data: areasData } = useQuery({
    queryKey: ['company-areas', { active: true }],
    queryFn: () => companyAreaService.getAreas({ active: true, page: 1, limit: 100 }),
  });

  // Buscar todos os usuários (quando "Todas as Áreas" está selecionada)
  const { data: allUsersResponse } = useQuery({
    queryKey: ['users', { active: true }],
    queryFn: async () => {
      const response = await usersApi.getAll({ active: 'true', page: 1, limit: 1000 });
      return response.data;
    },
    enabled: selectedAreaId === 'all',
  });

  // Buscar usuários que têm OS na área selecionada (quando uma área específica está selecionada)
  const { data: usersByArea } = useQuery({
    queryKey: ['users-by-area', selectedAreaId],
    queryFn: () => osReportsApi.getUsersByArea(selectedAreaId),
    enabled: selectedAreaId !== 'all',
  });

  // Determinar quais usuários mostrar
  const availableUsers = selectedAreaId === 'all' 
    ? allUsersResponse?.data || []
    : usersByArea || [];

  // Buscar relatório de OS por área
  const { data: reportData, isLoading, refetch } = useQuery<OSByAreaReport>({
    queryKey: ['os-by-area-report', selectedAreaId, selectedUserId],
    queryFn: () => osReportsApi.getOSByAreaReport(
      selectedAreaId === 'all' ? undefined : selectedAreaId,
      selectedUserId === 'all' ? undefined : selectedUserId
    ),
  });

  const toggleArea = (areaId: string | null) => {
    const key = areaId || 'sem-area';
    setExpandedAreas(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const handleClassifyOS = async (osId: string, areaId: string) => {
    if (!areaId || areaId === '') {
      toast.error('Selecione uma área para classificar a OS');
      return;
    }

    try {
      setClassifyingOS(osId);
      await updateOS(osId, { companyAreaId: areaId });
      toast.success('OS classificada com sucesso!');
      
      // Invalidar queries para atualizar o relatório
      queryClient.invalidateQueries({ queryKey: ['os-by-area-report'] });
      setClassifyingOS(null);
      setSelectedAreaForOS(prev => {
        const next = { ...prev };
        delete next[osId];
        return next;
      });
    } catch (error: any) {
      console.error('Erro ao classificar OS:', error);
      toast.error(error?.response?.data?.message || 'Erro ao classificar OS');
      setClassifyingOS(null);
    }
  };

  const exportToPDF = async () => {
    try {
      toast.loading('Gerando PDF...', { id: 'pdf-export' });
      
      if (!reportData) {
        throw new Error('Nenhum dado disponível para exportar');
      }

      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 15;
      const maxWidth = pageWidth - (margin * 2);
      let yPosition = margin;
      const lineHeight = 6; // Aumentado para melhor espaçamento entre linhas
      const sectionSpacing = 8; // Aumentado para melhor separação entre seções
      const elementSpacing = 3; // Espaçamento entre elementos dentro de uma seção

      // Função para adicionar nova página se necessário (com margem de segurança)
      const checkNewPage = (requiredSpace: number) => {
        const safetyMargin = 10; // Margem de segurança
        if (yPosition + requiredSpace + safetyMargin > pageHeight - margin) {
          pdf.addPage();
          yPosition = margin;
          return true;
        }
        return false;
      };

      // Função para adicionar texto com verificação de página
      const addTextWithPageCheck = (text: string, fontSize: number, isBold: boolean = false, color: string = '#000000') => {
        pdf.setFontSize(fontSize);
        pdf.setTextColor(color);
        if (isBold) {
          pdf.setFont('helvetica', 'bold');
        } else {
          pdf.setFont('helvetica', 'normal');
        }
        
        const lines = pdf.splitTextToSize(text, maxWidth);
        const totalHeight = lines.length * lineHeight;
        
        // Verificar se precisa de nova página antes de adicionar
        checkNewPage(totalHeight);
        
        lines.forEach((line: string) => {
          // Verificar novamente antes de cada linha (caso tenha quebrado)
          checkNewPage(lineHeight);
          pdf.text(line, margin, yPosition);
          yPosition += lineHeight;
        });
      };

      // Função para adicionar texto com quebra de linha
      const addText = (text: string, fontSize: number, isBold: boolean = false, color: string = '#000000') => {
        pdf.setFontSize(fontSize);
        pdf.setTextColor(color);
        if (isBold) {
          pdf.setFont('helvetica', 'bold');
        } else {
          pdf.setFont('helvetica', 'normal');
        }
        
        const lines = pdf.splitTextToSize(text, maxWidth);
        lines.forEach((line: string) => {
          checkNewPage(lineHeight);
          pdf.text(line, margin, yPosition);
          yPosition += lineHeight;
        });
      };

      // Cabeçalho
      pdf.setFillColor(41, 128, 185);
      pdf.rect(0, 0, pageWidth, 30, 'F');
      pdf.setTextColor('#FFFFFF');
      pdf.setFontSize(18);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Relatório de JOB/TASK por Área', margin, 20);
      
      yPosition = 35; // Reduzido de 40 para 35
      pdf.setTextColor('#000000');
      
      // Informações do relatório (em uma linha mais compacta)
      const areaName = selectedAreaId === 'all' 
        ? 'Todas as Áreas' 
        : areasData?.data.find(a => a.id === selectedAreaId)?.name || 'Área';
      const date = new Date().toLocaleDateString('pt-BR');
      
      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor('#666666');
      pdf.text(`Área: ${areaName} | Data: ${date} | Total JOB/TASK: ${reportData.summary.totalOS} | Horas: ${reportData.summary.formattedTotalHours}`, margin, yPosition);
      yPosition += lineHeight + 2; // Espaço reduzido

      // Processar cada área e suas OS
      reportData.report.forEach((areaGroup, areaIndex) => {
        // Título da Área (mais compacto)
        const areaTitleHeight = 8; // Altura total do título da área
        checkNewPage(areaTitleHeight);
        yPosition += 2;
        pdf.setFillColor(230, 230, 230);
        pdf.rect(margin, yPosition - 3, maxWidth, 6, 'F');
        pdf.setFontSize(11);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor('#000000');
        pdf.text(`ÁREA: ${areaGroup.area.name}`, margin + 2, yPosition + 2);
        yPosition += 5;

        // Processar cada OS da área
        areaGroup.ordersOfService.forEach((os, osIndex) => {
          // Separador entre OS (mais compacto, apenas se necessário)
          if (osIndex > 0) {
            // Verificar se há espaço para separador
            if (yPosition + 3 <= pageHeight - margin - 10) {
              checkNewPage(3);
              yPosition += 1;
              pdf.setDrawColor(200, 200, 200);
              pdf.line(margin, yPosition, pageWidth - margin, yPosition);
              yPosition += 1;
            } else {
              // Se não couber, criar nova página
              checkNewPage(3);
            }
          }

          // Espaçamento antes do título do JOB/TASK
          yPosition += elementSpacing;
          
          // Título do JOB/TASK
          pdf.setFontSize(10);
          pdf.setFont('helvetica', 'bold');
          pdf.setTextColor('#000000');
          const titleText = `JOB/TASK: ${os.title}`;
          const titleLines = pdf.splitTextToSize(titleText, maxWidth);
          const titleHeight = titleLines.length * lineHeight;
          
          // Verificar espaço antes de adicionar título
          checkNewPage(titleHeight + elementSpacing);
          
          // Resetar configurações após possível quebra de página
          pdf.setFontSize(10);
          pdf.setFont('helvetica', 'bold');
          pdf.setTextColor('#000000');
          
          titleLines.forEach((line: string) => {
            // Verificar antes de cada linha
            checkNewPage(lineHeight);
            // Resetar configurações após possível quebra
            pdf.setFontSize(10);
            pdf.setFont('helvetica', 'bold');
            pdf.setTextColor('#000000');
            pdf.text(line, margin, yPosition);
            yPosition += lineHeight;
          });
          
          // Espaçamento após título
          yPosition += elementSpacing;
          
          // Descrição do JOB/TASK
          if (os.description) {
            pdf.setFontSize(9);
            pdf.setFont('helvetica', 'normal');
            pdf.setTextColor('#000000');
            const descText = `Descrição: ${os.description}`;
            const descLines = pdf.splitTextToSize(descText, maxWidth);
            const descHeight = descLines.length * lineHeight;
            
            // Verificar espaço antes de adicionar descrição
            checkNewPage(descHeight);
            
            // Resetar configurações após possível quebra de página
            pdf.setFontSize(9);
            pdf.setFont('helvetica', 'normal');
            pdf.setTextColor('#000000');
            
            descLines.forEach((line: string) => {
              // Verificar antes de cada linha
              checkNewPage(lineHeight);
              // Resetar configurações após possível quebra
              pdf.setFontSize(9);
              pdf.setFont('helvetica', 'normal');
              pdf.setTextColor('#000000');
              pdf.text(line, margin, yPosition);
              yPosition += lineHeight;
            });
            
            // Espaçamento após descrição
            yPosition += elementSpacing;
          }

          // Informações do JOB/TASK distribuídas em até 3 linhas
          pdf.setFontSize(9);
          pdf.setFont('helvetica', 'normal');
          pdf.setTextColor('#000000');
          
          // Linha 1: Responsável JOB/TASK | Data Criação | Total Horas
          const createdAt = os.createdAt 
            ? new Date(os.createdAt).toLocaleDateString('pt-BR', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
              })
            : null;
          
          const line1Parts: string[] = [];
          line1Parts.push(`Responsável: ${os.user.name}`);
          if (createdAt) {
            line1Parts.push(`Data: ${createdAt}`);
          }
          line1Parts.push(`Horas: ${os.formattedHours}`);
          
          const line1Text = line1Parts.join(' | ');
          const line1Lines = pdf.splitTextToSize(line1Text, maxWidth);
          const line1Height = line1Lines.length * lineHeight;
          
          // Verificar espaço antes de adicionar
          checkNewPage(line1Height);
          
          // Resetar configurações após possível quebra de página
          pdf.setFontSize(9);
          pdf.setFont('helvetica', 'normal');
          pdf.setTextColor('#000000');
          
          line1Lines.forEach((line: string) => {
            // Verificar antes de cada linha
            checkNewPage(lineHeight);
            // Resetar configurações após possível quebra
            pdf.setFontSize(9);
            pdf.setFont('helvetica', 'normal');
            pdf.setTextColor('#000000');
            pdf.text(line, margin, yPosition);
            yPosition += lineHeight;
          });
          
          // Espaçamento após linha 1
          yPosition += elementSpacing / 2;

          // Linha 2: Demanda | Projeto
          const line2Parts: string[] = [];
          if (os.demand) {
            line2Parts.push(`Demanda: ${os.demand.title}`);
          }
          if (os.project) {
            line2Parts.push(`Projeto: ${os.project.name}`);
          }
          
          if (line2Parts.length > 0) {
            const line2Text = line2Parts.join(' | ');
            const line2Lines = pdf.splitTextToSize(line2Text, maxWidth);
            const line2Height = line2Lines.length * lineHeight;
            
            // Verificar espaço antes de adicionar
            checkNewPage(line2Height);
            
            // Resetar configurações após possível quebra de página
            pdf.setFontSize(9);
            pdf.setFont('helvetica', 'normal');
            pdf.setTextColor('#000000');
            
            line2Lines.forEach((line: string) => {
              // Verificar antes de cada linha
              checkNewPage(lineHeight);
              // Resetar configurações após possível quebra
              pdf.setFontSize(9);
              pdf.setFont('helvetica', 'normal');
              pdf.setTextColor('#000000');
              pdf.text(line, margin, yPosition);
              yPosition += lineHeight;
            });
            
            // Espaçamento após linha 2
            yPosition += elementSpacing / 2;
          }

          // Linha 3: Área Solicitante (com destaque em cor)
          if (os.companyArea) {
            // Espaçamento antes da área solicitante
            yPosition += elementSpacing;
            
            const areaHeight = lineHeight + 2; // Altura total do retângulo (aumentada)
            checkNewPage(areaHeight + elementSpacing); // Verificar espaço antes de adicionar área
            
            // Fundo colorido para destacar
            pdf.setFillColor(52, 152, 219); // Azul
            pdf.rect(margin, yPosition, maxWidth, lineHeight + 2, 'F');
            
            // Texto em branco para contraste (centralizado verticalmente)
            pdf.setTextColor('#FFFFFF');
            pdf.setFont('helvetica', 'bold');
            pdf.setFontSize(9);
            pdf.text(`Área Solicitante: ${os.companyArea.name}`, margin + 2, yPosition + 4);
            yPosition += lineHeight + 2;
            
            // Resetar cor do texto
            pdf.setTextColor('#000000');
            
            // Espaçamento após área solicitante
            yPosition += elementSpacing;
            
            // Se houver comentários, adicionar espaçamento adicional antes da barra de comentários
            if (os.comments && os.comments.length > 0) {
              yPosition += elementSpacing; // Espaçamento entre as duas barras azuis
            }
          }

          // Comentários (com barra azul similar à "Área Solicitante")
          if (os.comments && os.comments.length > 0) {
            // Espaçamento antes da seção de comentários (se não houver área solicitante antes)
            if (!os.companyArea) {
              yPosition += elementSpacing;
            }
            
            // Calcular altura necessária: barra + espaçamento + primeiro comentário
            const commentsHeaderHeight = lineHeight + 2; // Altura da barra azul (aumentada)
            const firstComment = os.comments[0];
            pdf.setFontSize(8);
            const firstCommentLines = pdf.splitTextToSize(firstComment.comment, maxWidth - 4);
            // Altura: nome (5mm) + espaçamento (3mm) + linhas do texto (lineHeight cada) + padding inferior (2mm - reduzido)
            const firstCommentHeight = 5 + 3 + (firstCommentLines.length * lineHeight) + 2;
            const totalNeeded = commentsHeaderHeight + elementSpacing * 3 + firstCommentHeight;
            
            checkNewPage(totalNeeded); // Verificar espaço antes de adicionar seção de comentários
            
            // Desenhar barra azul para "Comentários:"
            pdf.setFillColor(52, 152, 219); // Azul (mesma cor da "Área Solicitante")
            pdf.rect(margin, yPosition, maxWidth, lineHeight + 2, 'F');
            
            // Texto "Comentários:" em branco (centralizado verticalmente)
            pdf.setTextColor('#FFFFFF');
            pdf.setFont('helvetica', 'bold');
            pdf.setFontSize(9);
            pdf.text('Comentários:', margin + 2, yPosition + 4);
            yPosition += lineHeight + 2; // Avançar após a barra
            
            // Resetar cor do texto
            pdf.setTextColor('#000000');
            
            // Espaço adicional após a barra antes do primeiro comentário (evitar sobreposição)
            // Espaçamento MUITO maior para garantir que o texto não sobreponha a barra azul
            yPosition += elementSpacing * 3; // Espaçamento triplo após a barra (9mm)

            os.comments.forEach((comment, commentIndex) => {
              // Espaço entre comentários (compacto mas com separação suficiente)
              if (commentIndex > 0) {
                yPosition += elementSpacing; // Espaçamento simples entre comentários (3mm) - mais compacto
              }
              
              // Calcular altura total do comentário ANTES de adicionar
              pdf.setFontSize(8);
              const userNameLine = `${comment.user.name} - ${comment.formattedDate}`;
              const commentLines = pdf.splitTextToSize(comment.comment, maxWidth - 4);
              
              // Altura total: nome (5mm do topo) + espaçamento (3mm) + linhas do comentário (lineHeight cada) + padding inferior (2mm - reduzido para mais compacto)
              const commentHeight = 5 + 3 + (commentLines.length * lineHeight) + 2;
              
              // Verificar espaço antes de adicionar comentário (com margem de segurança aumentada)
              // Se não couber, criar nova página ANTES de desenhar qualquer coisa
              const pageWasAdded = checkNewPage(commentHeight + elementSpacing);
              
              // Verificar se o comentário inteiro cabe na página atual (após possível quebra)
              const willFitOnPage = (yPosition + commentHeight + elementSpacing) <= (pageHeight - margin - 15);
              
              // Posição inicial do comentário (após verificação de página)
              const commentStartY = yPosition;
              
              // Resetar configurações
              pdf.setFontSize(8);
              pdf.setFillColor(245, 245, 245);
              pdf.setTextColor('#000000');
              
              // Desenhar fundo cinza claro para comentário (apenas se couber na página)
              if (willFitOnPage) {
                pdf.rect(margin, commentStartY, maxWidth, commentHeight, 'F');
              }
              
              // Nome do usuário e data (posicionamento: 5mm do topo do retângulo - aumentado significativamente)
              pdf.setFont('helvetica', 'bold');
              pdf.setFontSize(8);
              pdf.setTextColor('#000000');
              pdf.text(userNameLine, margin + 2, commentStartY + 5);
              
              // Texto do comentário (posicionamento: 8mm do topo, com espaçamento de 3mm após o nome)
              pdf.setFont('helvetica', 'normal');
              pdf.setFontSize(8);
              pdf.setTextColor('#000000');
              
              // Adicionar cada linha do comentário
              // Posição inicial do texto: 8mm abaixo do início do comentário (5mm nome + 3mm espaçamento)
              let currentTextY = commentStartY + 8;
              let commentBrokePage = false;
              
              commentLines.forEach((line: string, lineIndex: number) => {
                // Verificar se esta linha cabe na página atual ANTES de desenhar
                // Usar margem maior para evitar que fique muito próximo da borda
                if (currentTextY + lineHeight > pageHeight - margin - 15) {
                  // Não cabe, criar nova página
                  pdf.addPage();
                  yPosition = margin;
                  
                  if (!commentBrokePage) {
                    // Primeira vez que quebra - redesenhar nome do usuário na nova página
                    commentBrokePage = true;
                    currentTextY = yPosition + elementSpacing * 2;
                    
                    // Redesenhar nome do usuário na nova página
                    pdf.setFont('helvetica', 'bold');
                    pdf.setFontSize(8);
                    pdf.setTextColor('#000000');
                    pdf.text(userNameLine, margin + 2, currentTextY);
                    currentTextY += 3; // Espaçamento de 3mm após o nome
                    currentTextY += lineHeight; // Mais um lineHeight para garantir espaço
                    
                    pdf.setFont('helvetica', 'normal');
                    pdf.setFontSize(8);
                    pdf.setTextColor('#000000');
                  } else {
                    // Já quebrou antes, apenas continuar na nova página com espaçamento
                    currentTextY = yPosition + elementSpacing * 2;
                  }
                  
                  // Resetar configurações de fonte
                  pdf.setFontSize(8);
                  pdf.setFont('helvetica', 'normal');
                  pdf.setTextColor('#000000');
                }
                
                // Desenhar a linha do comentário na posição atual
                pdf.text(line, margin + 2, currentTextY);
                
                // IMPORTANTE: Avançar para próxima linha ANTES de verificar próxima iteração
                currentTextY += lineHeight;
              });
              
              // Atualizar yPosition para o final do comentário
              // Se o comentário coube inteiro na página original
              if (!commentBrokePage && willFitOnPage && (commentStartY + commentHeight <= pageHeight - margin - 15)) {
                yPosition = commentStartY + commentHeight;
              } else {
                // Comentário quebrou página ou não coube inteiro
                // Usar a posição atual do texto + pequeno espaçamento
                yPosition = currentTextY + 1; // Espaçamento mínimo após comentário quebrado
              }
            });
            
            // Espaçamento mínimo após todos os comentários (apenas se necessário)
            // Não adicionar espaçamento extra para manter compacto
          }
        });

        // Sem espaço extra entre áreas (apenas se couber na página)
        if (areaIndex < reportData.report.length - 1) {
          // Verificar se há espaço para um pequeno separador
          if (yPosition + 2 <= pageHeight - margin - 10) {
            yPosition += 2;
          }
        }
      });

      // Rodapé
      const totalPages = pdf.internal.pages.length - 1;
      for (let i = 1; i <= totalPages; i++) {
        pdf.setPage(i);
        pdf.setFontSize(8);
        pdf.setTextColor('#666666');
        pdf.text(
          `Página ${i} de ${totalPages} - Gerado em ${new Date().toLocaleString('pt-BR')}`,
          pageWidth / 2,
          pageHeight - 10,
          { align: 'center' }
        );
      }

      // Salvar PDF
      const areaNameFile = selectedAreaId === 'all' 
        ? 'todas-areas' 
        : areasData?.data.find(a => a.id === selectedAreaId)?.name.replace(/\s+/g, '-').toLowerCase() || 'area';
      const dateFile = new Date().toISOString().split('T')[0];
      pdf.save(`relatorio-os-por-area-${areaNameFile}-${dateFile}.pdf`);
      
      toast.success('PDF gerado com sucesso!', { id: 'pdf-export' });
    } catch (error) {
      console.error('Erro ao gerar PDF:', error);
      toast.error('Erro ao gerar PDF', { id: 'pdf-export' });
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  if (!reportData) {
    return (
      <div className="text-center py-12">
        <p className="text-text-muted">Nenhum dado disponível</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="md:flex md:items-center md:justify-between">
        <div className="flex-1 min-w-0">
          <h2 className="text-xl font-semibold leading-6 text-text-primary sm:text-2xl sm:truncate">
            Relatório de OS por Área
          </h2>
          <p className="mt-1 text-sm text-text-muted">
            Visualize Ordens de Serviço separadas por área da empresa
          </p>
        </div>
        <div className="mt-4 flex md:mt-0 md:ml-4 space-x-3">
          <button
            onClick={() => refetch()}
            className="inline-flex items-center px-4 py-2 border border-border-glass rounded-button shadow-glass text-sm font-bold text-text-secondary bg-background-glass backdrop-blur-glass hover:bg-background-glass-hover focus:outline-none focus:ring-2 focus:ring-offset-background-dark focus:ring-blue-500"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Atualizar
          </button>
          <button
            onClick={exportToPDF}
            className="inline-flex items-center px-4 py-2 border border-border-glass rounded-button shadow-glass text-sm font-bold text-text-secondary bg-background-glass backdrop-blur-glass hover:bg-background-glass-hover focus:outline-none focus:ring-2 focus:ring-offset-background-dark focus:ring-blue-500"
          >
            <Download className="h-4 w-4 mr-2" />
            Exportar PDF
          </button>
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-background-glass backdrop-blur-glass shadow rounded-glass p-4">
        <div className="flex items-center space-x-4">
          <Filter className="h-5 w-5 text-text-muted" />
          <h3 className="text-lg font-bold text-text-primary">Filtros</h3>
        </div>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="area" className="block text-sm font-bold text-text-secondary mb-2">
              Área da Empresa
            </label>
            <select
              id="area"
              value={selectedAreaId}
              onChange={(e) => {
                setSelectedAreaId(e.target.value);
                // Resetar usuário selecionado quando área mudar
                setSelectedUserId('all');
              }}
              className="input block w-full"
            >
              <option value="all">Todas as Áreas</option>
              {areasData?.data.map(area => (
                <option key={area.id} value={area.id}>
                  {area.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="user" className="block text-sm font-bold text-text-secondary mb-2">
              Usuário Responsável
            </label>
            <select
              id="user"
              value={selectedUserId}
              onChange={(e) => setSelectedUserId(e.target.value)}
              className="input block w-full"
              disabled={selectedAreaId !== 'all' && availableUsers.length === 0}
            >
              <option value="all">
                {selectedAreaId === 'all' ? 'Todos os Usuários' : 'Todos os Usuários da Área'}
              </option>
              {availableUsers.map((user: any) => (
                <option key={user.id} value={user.id}>
                  {user.name} {user.email ? `(${user.email})` : ''}
                </option>
              ))}
            </select>
            {selectedAreaId !== 'all' && availableUsers.length === 0 && (
              <p className="mt-1 text-xs text-text-muted">
                Nenhum usuário com OS nesta área
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Resumo */}
      <div className="bg-background-glass backdrop-blur-glass shadow rounded-glass p-6">
        <h3 className="text-lg font-bold text-text-primary mb-4">Resumo</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <p className="text-sm text-text-muted">Total de OS</p>
            <p className="text-2xl font-bold text-text-primary">{reportData.summary.totalOS}</p>
          </div>
          <div>
            <p className="text-sm text-text-muted">Total de Horas</p>
            <p className="text-2xl font-bold text-text-primary">{reportData.summary.formattedTotalHours}</p>
          </div>
          <div>
            <p className="text-sm text-text-muted">Total de Comentários</p>
            <p className="text-2xl font-bold text-text-primary">{reportData.summary.totalComments}</p>
          </div>
          <div>
            <p className="text-sm text-text-muted">Áreas</p>
            <p className="text-2xl font-bold text-text-primary">{reportData.summary.areasCount}</p>
          </div>
        </div>
      </div>

      {/* Conteúdo do Relatório */}
      <div id="report-content" className="bg-background-glass backdrop-blur-glass shadow rounded-glass p-6">
        <h3 className="text-lg font-bold text-text-primary mb-4">Relatório Detalhado</h3>
        
        {reportData.report.map((areaGroup) => {
          const areaKey = areaGroup.area.id || 'sem-area';
          const isExpanded = expandedAreas.has(areaKey);
          
          return (
            <div key={areaKey} className="mb-6 border-b border-border-glass pb-4 last:border-b-0">
              <button
                onClick={() => toggleArea(areaGroup.area.id)}
                className="w-full flex items-center justify-between p-4 bg-background-glass-hover rounded-button hover:bg-background-glass-hover transition-colors"
              >
                <div className="flex-1 text-left">
                  <h4 className="text-lg font-bold text-text-primary">{areaGroup.area.name}</h4>
                  <p className="text-sm text-text-muted">
                    {areaGroup.ordersOfService.length} OS • {areaGroup.ordersOfService.reduce((sum, os) => sum + os.actualHours, 0).toFixed(2)}h
                  </p>
                </div>
                {isExpanded ? (
                  <ChevronUp className="h-5 w-5 text-text-muted" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-text-muted" />
                )}
              </button>

              {isExpanded && (
                <div className="mt-4 space-y-4">
                  {areaGroup.ordersOfService.map((os) => (
                    <div key={os.id} className="bg-background-glass-hover rounded-button p-4">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1">
                          <h5 className="font-bold text-text-primary">{os.title}</h5>
                          <p className="text-sm text-text-muted mt-1">{os.description}</p>
                        </div>
                        <div className="ml-4 text-right">
                          <p className="text-sm font-bold text-text-primary">{os.formattedHours}</p>
                          <p className="text-xs text-text-muted">horas trabalhadas</p>
                        </div>
                      </div>

                      {/* Botão para classificar OS sem área */}
                      {!os.companyArea && (
                        <div className="mt-3 p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-button border border-yellow-200 dark:border-yellow-800">
                          <div className="flex items-center gap-3">
                            <Tag className="h-4 w-4 text-yellow-600 dark:text-yellow-400" />
                            <div className="flex-1">
                              <p className="text-sm font-bold text-yellow-800 dark:text-yellow-200 mb-2">
                                Classificar OS para uma área
                              </p>
                              <div className="flex items-center gap-2">
                                <select
                                  value={selectedAreaForOS[os.id] || ''}
                                  onChange={(e) => setSelectedAreaForOS(prev => ({
                                    ...prev,
                                    [os.id]: e.target.value
                                  }))}
                                  className="input flex-1 text-sm"
                                  disabled={classifyingOS === os.id}
                                >
                                  <option value="">Selecione uma área...</option>
                                  {areasData?.data.map(area => (
                                    <option key={area.id} value={area.id}>
                                      {area.name}
                                    </option>
                                  ))}
                                </select>
                                <button
                                  onClick={() => handleClassifyOS(os.id, selectedAreaForOS[os.id] || '')}
                                  disabled={!selectedAreaForOS[os.id] || classifyingOS === os.id}
                                  className="px-3 py-1.5 text-sm font-bold text-white bg-blue-600 hover:bg-blue-700 rounded-button disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                  {classifyingOS === os.id ? 'Classificando...' : 'Classificar'}
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4 text-sm">
                        <div>
                          <p className="text-text-muted">Status:</p>
                          <p className="font-bold text-text-primary">{os.status}</p>
                        </div>
                        <div>
                          <p className="text-text-muted">Tipo:</p>
                          <p className="font-bold text-text-primary">{os.type}</p>
                        </div>
                        {os.formattedStartDate && (
                          <div>
                            <p className="text-text-muted">Data de Início:</p>
                            <p className="font-bold text-text-primary">{os.formattedStartDate}</p>
                          </div>
                        )}
                        {os.demand && (
                          <div>
                            <p className="text-text-muted">Demanda:</p>
                            <p className="font-bold text-text-primary">{os.demand.title}</p>
                          </div>
                        )}
                        {os.project && (
                          <div>
                            <p className="text-text-muted">Projeto:</p>
                            <p className="font-bold text-text-primary">{os.project.name}</p>
                          </div>
                        )}
                      </div>

                      {os.comments.length > 0 && (
                        <div className="mt-4">
                          <p className="text-sm font-bold text-text-secondary mb-2">
                            Comentários ({os.commentsCount})
                          </p>
                          <div className="space-y-2">
                            {os.comments.map((comment) => (
                              <div key={comment.id} className="bg-background-glass rounded-button p-3">
                                <div className="flex items-start justify-between mb-1">
                                  <p className="text-sm font-bold text-text-primary">{comment.user.name}</p>
                                  <p className="text-xs text-text-muted">{comment.formattedDate}</p>
                                </div>
                                <p className="text-sm text-text-secondary">{comment.comment}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

